import { Box, Button, Stack, Typography } from '@mui/material';
import Alert from '/@/components/bayer/alert';
import { useCornTechnologies } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageAction } from './shared';
import { useBarbechoStageMessage } from './use-barbecho-stage-message';
import { useCriticPeriodStageMessage } from './use-critic-period-stage-message';
import { useEmergenciaStageMessage } from './use-emergencia-stage-message';
import { useMaturityStageMessage } from './use-maturity-stage-message';
import { usePastHarvestDateStageMessage } from './use-past-harvest-date-stage-message';
import { usePostMaturityHarvestStageMessage } from './use-post-maturity-harvest-stage-message';
import { useSiembraStageMessage } from './use-siembra-stage-message';
import { useV2StageMessage } from './use-v2-stage-message';
import { useV3StageMessage } from './use-v3-stage-message';
import { useV5StageMessage } from './use-v5-stage-message';
import { useV7StageMessage } from './use-v7-stage-message';
import { useVNStageMessage } from './use-vn-stage-message';

/**
 * Stage-aware banner shown above the field-monitoring detail. It routes on the field's crop
 * stage and renders the matching message with its actions. Barbecho (fallow), Siembra
 * (pre-planting), Emergencia (corn in VE), V2/V3/V5/V7/VN (corn vegetative stages), the critical
 * period, MF (maturity), post-maturity harvest (30+ days after MF), and past-harvest-date are
 * implemented; they're mutually exclusive. Every other stage renders nothing until it's added.
 */
const StageInfoMessage = ({
    cropMonitoring,
    field,
    onOpenHarvestReport,
    onRegisterApplication,
    onRecommendHerbicide,
    onCreateVisitReport,
    onScheduleVisit,
    onRequestNSmart,
    onGenerateRecommendation,
    onConfirmNSmartDate,
    onRecommendFungicide,
    onViewSamplingGuide,
    onEstimateYield,
    onFinalizeCampaignWithoutData,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
    onOpenHarvestReport: () => void;
    onRegisterApplication: () => void;
    onRecommendHerbicide: () => void;
    onCreateVisitReport: () => void;
    onScheduleVisit: () => void;
    onRequestNSmart: () => void;
    onGenerateRecommendation: () => void;
    onConfirmNSmartDate: () => void;
    onRecommendFungicide: () => void;
    onViewSamplingGuide: () => void;
    onEstimateYield: () => void;
    onFinalizeCampaignWithoutData: () => void;
}) => {
    const barbecho = useBarbechoStageMessage({ cropMonitoring, field });
    const siembra = useSiembraStageMessage({ cropMonitoring, field });
    const emergencia = useEmergenciaStageMessage({ cropMonitoring, field });
    const v2 = useV2StageMessage({ cropMonitoring, field });
    const v3 = useV3StageMessage({ cropMonitoring, field });
    const v5 = useV5StageMessage({ cropMonitoring, field });
    const v7 = useV7StageMessage({ cropMonitoring, field });
    const criticPeriod = useCriticPeriodStageMessage({ cropMonitoring, field });
    const vn = useVNStageMessage({ cropMonitoring, field });
    const pastHarvestDate = usePastHarvestDateStageMessage({ cropMonitoring, field });
    const postMaturityHarvest = usePostMaturityHarvestStageMessage({ cropMonitoring, field });
    const maturity = useMaturityStageMessage({ cropMonitoring, field });
    const stage =
        barbecho ??
        siembra ??
        emergencia ??
        v2 ??
        v3 ??
        v5 ??
        v7 ??
        criticPeriod ??
        vn ??
        pastHarvestDate ??
        postMaturityHarvest ??
        maturity;

    // Glyphosate can't be applied to VTPRO4 corn hybrids from V6 on — a standing reminder shown
    // alongside whatever stage banner applies, and on its own at V6 (which has no stage banner).
    const technologiesQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const cropHasVtpro4 = (technologiesQuery.data?.results ?? []).some(
        (tech: { name?: string }) => tech.name === 'VTPRO4'
    );
    const vegetativeStage = cropMonitoring?.cornCropMonitoring?.vegetativeStage;
    const showVtpro4Warning =
        cropHasVtpro4 &&
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        vegetativeStage != null &&
        vegetativeStage >= 6;

    if (!stage && !showVtpro4Warning) return null;

    const handlers: Record<StageAction, () => void> = {
        'harvest-report': onOpenHarvestReport,
        'register-application': onRegisterApplication,
        'recommend-herbicide': onRecommendHerbicide,
        'create-visit-report': onCreateVisitReport,
        'schedule-visit': onScheduleVisit,
        'request-nsmart': onRequestNSmart,
        'generate-cp-recommendation': onGenerateRecommendation,
        'confirm-nsmart-date': onConfirmNSmartDate,
        'recommend-fungicide': onRecommendFungicide,
        'view-sampling-guide': onViewSamplingGuide,
        'estimate-yield': onEstimateYield,
        'finalize-campaign-without-data': onFinalizeCampaignWithoutData,
    };

    return (
        <Stack direction='column' spacing={3}>
            {stage && (
                <Stack
                    direction='column'
                    spacing={1.5}
                    sx={{ background: (t) => t.palette.primary.infoBackground }}
                    p={2}
                >
                    <Stack direction='column'>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {stage.title}
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {stage.message}
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1.5}>
                        {stage.secondaryAction && (
                            <Box sx={{ background: 'white' }}>
                                <Button variant='text' color='primary' onClick={handlers[stage.secondaryAction.action]}>
                                    {stage.secondaryAction.label}
                                </Button>
                            </Box>
                        )}
                        {stage.primaryAction && (
                            <Button variant='contained' color='primary' onClick={handlers[stage.primaryAction.action]}>
                                {stage.primaryAction.label}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            )}
            {showVtpro4Warning && (
                <Alert severity='danger'>
                    {_(
                        'El híbrido sembrado en el lote tiene tecnología VTPRO4. Recordale al productor que no puede aplicar productos a base de glifosato a partir de V6.'
                    )}
                </Alert>
            )}
        </Stack>
    );
};

export default StageInfoMessage;
